/** @jsxImportSource theme-ui */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import diseasesSelectors from 'diseases/diseases.selectors';
import locationsSelectors from 'locations/locations.selectors';
import { fetchCustomSettingsInfo, fetchUserProfile, updateCustomSettings } from 'user/user.saga';
import * as UserSelectors from 'user/user.selectors';

import { CustomSettingsForm } from './CustomSettingsForm';
import {
  MapCustomSettingsSubmitData2DtoPayload,
  MapDiseaseRelevance2DiseaseNotificationLevelDict,
} from './CustomSettingsHelpers';
import { CustomSettingsSubmitData, RoleAndItsPresets } from './CustomSettingsModels';

export const CustomSettingsPage: React.FC = () => {
  const dispatch = useDispatch();

  const userProfile = useSelector(UserSelectors.userProfileSelector);
  const roles = useSelector(UserSelectors.rolesSelector);
  const geonames = useSelector(locationsSelectors.getAllLocations);
  const diseases = useSelector(diseasesSelectors.allAvailableDiseasesById);
  const diseaseGroups = useSelector(diseasesSelectors.getDiseaseGroups);

  const userRoleIdInitial = userProfile && userProfile.userType.id;

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchCustomSettingsInfo());
  }, []);

  const rolesAndPresets = useMemo(
    () =>
      roles &&
      roles.map(
        role =>
          ({
            id: role.id,
            name: role.name,
            notificationDescription: role.notificationDescription,
            preset: MapDiseaseRelevance2DiseaseNotificationLevelDict(role.relevanceSettings),
          } as RoleAndItsPresets)
      ),
    [roles]
  );

  const userDiseaseRelevanceSetting = useMemo(
    () =>
      userProfile &&
      userProfile.diseaseRelevanceSetting &&
      MapDiseaseRelevance2DiseaseNotificationLevelDict(userProfile.diseaseRelevanceSetting),
    [userProfile]
  );

  const handleOnSubmit = (data: CustomSettingsSubmitData) => {
    dispatch(updateCustomSettings(MapCustomSettingsSubmitData2DtoPayload(data)));
  };

  return (
    <CustomSettingsForm
      rolesAndPresets={rolesAndPresets}
      diseases={diseases}
      diseaseGroupings={diseaseGroups}
      geonames={geonames}
      userRoleIdInitial={userRoleIdInitial}
      userDiseaseRelevanceSetting={userDiseaseRelevanceSetting}
      onSubmit={handleOnSubmit}
    />
  );
};
